'use client';

import React, { useCallback, useMemo, useState } from 'react';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { faCreditCard, faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import {
  FilterLabel,
  FilterIcon,
  IncludeExcludeSelect,
  FilterAutocomplete,
  Divider,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import {
  buildSocCombinedOptions,
  resolveSocSelection,
} from 'src/components/FilterSets/SiteOfCare/utils';
import useSocOptions from 'src/components/FilterSets/SiteOfCare/useSocOptions';
import {
  useGetPlaceOfServiceSuggestionsInfiniteQuery,
  useGetPayerTypeSuggestionsInfiniteQuery,
} from 'src/api/redux';

interface ColumnFilterWrapperProps {
  location: string;
  filterType: 'placeOfService' | 'payerType' | 'siteOfCare';
  smallLabel?: boolean;
}

/**
 * Wrapper component for PlaceOfService and PayerType filters
 * Uses column-specific filter state from useTableFilterSelections
 */
const ColumnFilterWrapper: React.FC<ColumnFilterWrapperProps> = ({
  location,
  filterType,
  smallLabel = false,
}) => {
  const {
    columnId,
    setPlaceOfServiceFilter,
    setPayerTypeFilter,
    setSiteOfCareFilter,
    allTempColumnSettings,
  } = useTableFilterSelections(location);

  // Get the current column settings
  const columnSettings = columnId ? allTempColumnSettings[columnId] : null;

  // Get the current filter value
  const placeOfServiceFilter = columnSettings?.placeOfServiceFilter || {
    type: null,
    is_excluded: false,
    is_all: false,
    values: [],
  };

  const payerTypeFilter = columnSettings?.payerTypeFilter || {
    type: null,
    is_excluded: false,
    is_all: false,
    values: [],
  };

  // Fall back to an empty default for state persisted before siteOfCareFilter
  // existed (see filterSelectionsSlice hydration backfill).
  const siteOfCareFilter = columnSettings?.siteOfCareFilter || {
    type: null,
    is_excluded: false,
    is_all: false,
    values: [],
  };

  // Render the appropriate filter component based on filterType
  if (filterType === 'placeOfService') {
    return (
      <ColumnPlaceOfService
        location={location}
        smallLabel={smallLabel}
        filter={placeOfServiceFilter}
        onChange={setPlaceOfServiceFilter}
        columnId={columnId}
      />
    );
  } else if (filterType === 'payerType') {
    return (
      <ColumnPayerType
        location={location}
        smallLabel={smallLabel}
        filter={payerTypeFilter}
        onChange={setPayerTypeFilter}
        columnId={columnId}
      />
    );
  } else if (filterType === 'siteOfCare') {
    return (
      <ColumnSiteOfCare
        location={location}
        smallLabel={smallLabel}
        filter={siteOfCareFilter}
        onChange={setSiteOfCareFilter}
        columnId={columnId}
      />
    );
  }

  return null;
};

// Custom PlaceOfService component for column-specific filters
interface ColumnFilterProps {
  location: string;
  smallLabel?: boolean;
  filter: {
    type: any | null;
    is_excluded: boolean;
    is_all: boolean;
    values: { value: string; label: string }[];
  };
  onChange: (filter: {
    type: any | null;
    is_excluded: boolean;
    is_all: boolean;
    values: { value: string; label: string }[];
  }) => void;
  columnId: string | null;
}

const ColumnPlaceOfService: React.FC<ColumnFilterProps> = ({
  location: _location,
  smallLabel = false,
  filter,
  onChange,
  columnId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useGetPlaceOfServiceSuggestionsInfiniteQuery({
      q: searchTerm,
    });

  const options = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      (page.results ?? []).map((result) => ({
        value: result.id,
        label: result.name,
      }))
    );
  }, [data]);

  const handleFilterChange = useCallback(
    (value: any) => {
      if (!columnId) return;
      onChange(value);
    },
    [columnId, onChange]
  );

  const handleRemoveFilter = useCallback(
    (filterLabel: string) => {
      if (!columnId) return;
      const newValues = filter.values.filter((v) => v.label !== filterLabel);
      onChange({
        ...filter,
        values: newValues,
      });
    },
    [columnId, filter, onChange]
  );

  const handleClearFilter = useCallback(() => {
    if (!columnId) return;
    onChange({
      type: null,
      is_excluded: false,
      is_all: false,
      values: [],
    });
  }, [columnId, onChange]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filter?.values?.length;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <FilterLabel smallLabel={smallLabel}>Place of Service</FilterLabel>
        <div
          className={`justify-start text-secondary text-xs font-normal font-['Inter'] leading-none cursor-pointer ${
            !hasValues ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={hasValues ? handleClearFilter : undefined}
        >
          Clear
        </div>
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faLocationDot} />
          <IncludeExcludeSelect
            title="Place of Service"
            value={filter}
            onChange={handleFilterChange}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filter}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search place of service..."
            onSearch={handleSearch}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetching}
            fetchNextPage={fetchNextPage}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filter?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filter?.is_all}
      />
    </div>
  );
};

// Custom PayerType component for column-specific filters
const ColumnPayerType: React.FC<ColumnFilterProps> = ({
  location: _location,
  smallLabel = false,
  filter,
  onChange,
  columnId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useGetPayerTypeSuggestionsInfiniteQuery({
      q: searchTerm,
    });

  const options = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      (page.results ?? []).map((result) => ({
        value: result.id,
        label: result.name,
      }))
    );
  }, [data]);

  const handleFilterChange = useCallback(
    (value: any) => {
      if (!columnId) return;
      onChange(value);
    },
    [columnId, onChange]
  );

  const handleRemoveFilter = useCallback(
    (filterLabel: string) => {
      if (!columnId) return;
      const newValues = filter.values.filter((v) => v.label !== filterLabel);
      onChange({
        ...filter,
        values: newValues,
      });
    },
    [columnId, filter, onChange]
  );

  const handleClearFilter = useCallback(() => {
    if (!columnId) return;
    onChange({
      type: null,
      is_excluded: false,
      is_all: false,
      values: [],
    });
  }, [columnId, onChange]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filter?.values?.length;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <FilterLabel smallLabel={smallLabel}>Payer Type</FilterLabel>
        <div
          className={`justify-start text-secondary text-xs font-normal font-['Inter'] leading-none cursor-pointer ${
            !hasValues ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={hasValues ? handleClearFilter : undefined}
        >
          Clear
        </div>
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faCreditCard} />
          <IncludeExcludeSelect
            title="Payer Type"
            value={filter}
            onChange={handleFilterChange}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filter}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search payer type..."
            onSearch={handleSearch}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetching}
            fetchNextPage={fetchNextPage}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filter?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filter?.is_all}
      />
    </div>
  );
};

// Custom SiteOfCare component for column-specific filters
const ColumnSiteOfCare: React.FC<ColumnFilterProps> = ({
  location: _location,
  smallLabel = false,
  filter,
  onChange,
  columnId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { socOptions, isLoading } = useSocOptions();

  const options = useMemo(() => {
    const all = buildSocCombinedOptions(socOptions);
    const term = searchTerm.trim().toLowerCase();
    if (!term) return all;
    return all.filter((option) => option.label.toLowerCase().includes(term));
  }, [socOptions, searchTerm]);

  const handleFilterChange = useCallback(
    (value: any) => {
      if (!columnId) return;
      onChange({
        ...value,
        values: resolveSocSelection(filter?.values ?? [], value?.values ?? []),
      });
    },
    [columnId, filter, onChange]
  );

  const handleRemoveFilter = useCallback(
    (filterLabel: string) => {
      if (!columnId) return;
      const newValues = filter.values.filter((v) => v.label !== filterLabel);
      onChange({
        ...filter,
        values: newValues,
      });
    },
    [columnId, filter, onChange]
  );

  const handleClearFilter = useCallback(() => {
    if (!columnId) return;
    onChange({
      type: null,
      is_excluded: false,
      is_all: false,
      values: [],
    });
  }, [columnId, onChange]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filter?.values?.length;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <FilterLabel smallLabel={smallLabel}>Site of Care Type</FilterLabel>
        <div
          className={`justify-start text-secondary text-xs font-normal font-['Inter'] leading-none cursor-pointer ${
            !hasValues ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={hasValues ? handleClearFilter : undefined}
        >
          Clear
        </div>
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faLocationDot} />
          <IncludeExcludeSelect
            title="Site of Care"
            value={filter}
            onChange={handleFilterChange}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filter}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search site of care..."
            onSearch={handleSearch}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filter?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filter?.is_all}
      />
    </div>
  );
};

export default ColumnFilterWrapper;
